import Modal, { ModalSize } from "../../../../common/components/Modal";
import AtcProductInfo from "../../v2/AtcProductInfo/AtcProductInfo";
import { PackagingSelection } from "../../../../common/components/PackagingSelection/PackagingSelection";
import {
  getCountriesListByProductDescriptor,
  getPriceInfo,
  isProductPMA,
} from "../../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { Permission, userHasAnyPermission, userHasPermission } from "../../../../utils/permissions";
import OptimeSolutionsMessage from "../../../../common/components/Messages/OptimeSolutionsMessage";
import DisclaimerCountryOrigin from "../../../../common/components/Messages/DisclaimerCountryOrigin";
import { useAddToCartWizardContext } from "../AddToCartWizard";
import { useFormContext } from "react-hook-form";
import { type AddToCartFormData } from "../../../hooks/useAddToCartNew";
import { useIsMinTabletSize, useIsMinDesktopSize } from "../../../../common/hooks/useMediaQuery";
import { isSpecialPrice } from "~/ProductDetails/redux/productDetailsSelectors";
import { IncludesSurchargeDisclaimer } from "~/common/components/Messages/IncludesSurchargeDisclaimer";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { type PdpSaleData } from "~/SalesCampaign/utils";
import { useSalesData } from "~/SalesCampaign/useSalesData";
import { Alert } from "~/common/m3/Alert/Alert";
import classNames from "classnames";
import AtcProductPriceInfo from "../../v2/AtcProductPriceInfo/AtcProductPriceInfo";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { Drawer, DialogActions, DialogContent, Typography, styled, IconButton, Stack } from "@mui/material";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

interface Props {
  productDescriptor: ProductDescriptor;
  submit: () => void;
  overQtyMessageEnabled?: boolean;
  sending: boolean;
}

export const AddToCartModal = ({ productDescriptor, submit, overQtyMessageEnabled = true, sending }: Props) => {
  const { t, getModalProps, getMainCls, editMode, getCloseBtn, getActionBtn, closePopup, restrictionQuery } =
    useAddToCartWizardContext();
  const { watch, setValue } = useFormContext<AddToCartFormData>();

  let okLabel;
  if (editMode) {
    okLabel = "quoteListPage.applyBtn";
  } else {
    if (isProductPMA(productDescriptor)) {
      okLabel = "cartPage.btn.quote";
    } else {
      okLabel = "addToCartPopup.add.submitBtn";
    }
  }
  okLabel = t(okLabel);

  const quantity = watch("quantity");
  const priceReference = watch("priceReference");
  const price = watch("netPrice");
  const priceUnit = watch("priceUnit");
  const hasSurcharge = watch("surcharge") > 0;

  const tariffSurcharge = watch("tariffSurcharge");

  const selectedPack = watch("selectedPack");
  const selectedPackCode = watch("selectedPackCode");

  const onSelectPackage = (packCode: string) => setValue("selectedPackCode", packCode);

  const isSpecial = isSpecialPrice(productDescriptor, selectedPackCode);

  const priceInfo = getPriceInfo({ product: productDescriptor, packCode: selectedPackCode });

  const showBetterDeal =
    overQtyMessageEnabled &&
    priceInfo &&
    priceInfo.minQtyAvailableScale !== null &&
    watch("totalQuantity") >= priceInfo.minQtyAvailableScale;

  const isPackageSizeInvalid = quantity === 0 || (selectedPack && !Number.isInteger(quantity / selectedPack.packSize));

  const addToCartDisabled = isPackageSizeInvalid || !selectedPack;
  const isSubmitEnabled = !addToCartDisabled;

  const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);
  const isTablet = useIsMinTabletSize();
  const isDesktop = useIsMinDesktopSize();

  const salesData = useSalesData();
  const saleForPack: PdpSaleData | undefined = salesData?.salePerPack[selectedPackCode];
  const saleActive = !!saleForPack && watch("salePriceEnabled");
  const exceedsSalesStock = !!saleForPack && quantity > saleForPack.salesStock;

  const renderSalesInfoMessage =
    saleActive && exceedsSalesStock
      ? (packCode: string) => {
          if (packCode === selectedPackCode) {
            return <Alert severity={"info"}>{t("feature.salesCampaign.exceedsQty")}</Alert>;
          }
          return null;
        }
      : undefined;

  const footer = (
    <Stack direction={"row"} gap={1} className={classNames(!isTablet && "w-100")}>
      {getCloseBtn(t("cancel"), !isTablet)}
      {showAddToCart && getActionBtn(okLabel, submit, !isSubmitEnabled || sending, "add-to-cart", !isTablet)}
    </Stack>
  );

  const content = (
    <div className="scha__atc-popup__wrapper">
      <div className={classNames(getMainCls(), "scha__atc-popup-continer")}>
        <div className="scha__atc-product-info-wrapper">
          <AtcProductInfo descriptor={productDescriptor} />
        </div>
        <div className="scha__atc-popup--packaging-selection-notifications-wrapper">
          <div className="scha__atc-popup--column-packaging__wrapper scha__atc-popup--column-packaging">
            <div className="scha__atc-popup--column-packaging">
              {isPackageSizeInvalid && (
                <Alert className="mb-3" severity="error">
                  {t("addToCartPopup.mismatchedQty")}
                </Alert>
              )}
              {showBetterDeal && (
                <Alert severity="info" className={"mb-2"}>
                  {t("addProduct.scalingDeal")}
                </Alert>
              )}
              <div className="scha__atc-packaging-opts">
                <div className="scha__atc-packaging-opts__title">{t("addToCartPopup.select.packaging.title")}</div>
                <PackagingSelection
                  selectedCode={selectedPackCode}
                  onSelectCode={onSelectPackage}
                  product={productDescriptor}
                  renderExtraInfoOnItem={renderSalesInfoMessage}
                  selectionType={"radio"}
                />
              </div>
            </div>
            <AtcProductPriceInfo
              quantity={quantity}
              price={price}
              onChange={(newQty) => setValue("quantity", newQty, { shouldDirty: true, shouldTouch: true })}
              invalidQuantity={isPackageSizeInvalid ?? undefined}
              priceUnit={priceUnit}
              priceReference={priceReference}
              onPriceReferenceChange={(newVal) => setValue("priceReference", newVal ?? "")}
              descriptor={productDescriptor}
              hasSurcharge={hasSurcharge}
              isSpecialPrice={isSpecial}
              showInfoOrderHandlingUnitQty={true}
              disabled={sending}
            />
          </div>
          <Stack gap={1} className="notifications scha__atc-popup--notifications">
            {isProductPMA(productDescriptor) && <InfoProductMessagePMA />}
            {userHasPermission(Permission.ADD_TO_CART) && <Alert severity="info" className="w-100">{t("addToCartPopup.disclaimer")}</Alert>}
            {productDescriptor.base.isOptime && <OptimeSolutionsMessage />}
            {getCountriesListByProductDescriptor({ descriptor: productDescriptor }).length > 0 && (
              <DisclaimerCountryOrigin />
            )}
            <IncludesSurchargeDisclaimer
              hasSurcharge={hasSurcharge}
              tariffSurcharge={tariffSurcharge}
              className="w-100"
            />
          </Stack>
        </div>
      </div>
    </div>
  );

  return isTablet ? (
    <Modal
      title={t("addToCartPopup.title")}
      size={isDesktop ? ModalSize.MEDIUM : ModalSize.TABLET}
      onClose={closePopup}
      noPadding={!isTablet}
      footerClassName={classNames(!isTablet && "scha__add-to-cart-popup-footer")}
      footerChildren={footer}
      {...getModalProps()}
    >
      {content}
    </Modal>
  ) : (
    <Drawer
      anchor="bottom"
      open={true}
      onClose={closePopup}
      slotProps={{ paper: { className: "scha__add-to-cart-popup" } }}
    >
      <DrawerHeader onClose={closePopup}>
        <Typography variant="titleLarge">{t("addToCartPopup.title")}</Typography>
      </DrawerHeader>
      <DrawerContent>{content}</DrawerContent>
      <DrawerActions>{footer}</DrawerActions>
    </Drawer>
  );
};

export const DrawerHeader = styled((props: JSX.IntrinsicElements["div"] & { onClose?: () => void }) => {
  const { onClose, children, ...rest } = props;
  return (
    <div {...rest}>
      <div>{children}</div>
      <IconButton variant="contained" color={"secondary"} size={"tiny"} onClick={onClose}>
        <CloseRoundedIcon />
      </IconButton>
    </div>
  );
})({
  padding: "16px 16px",
  display: "grid",
  gridTemplateColumns: "1fr auto",
  alignItems: "center",
  ["& .MuiIconButton-sizeMedium"]: {
    padding: 0,
  },
});

export const DrawerContent = styled(DialogContent)({
  padding: "0",
});

export const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
});
